import * as React from 'react';

import { getLocalTimeZone, today } from '@internationalized/date';
import {
  enumLoadStatus,
  LoadStatus,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { Button, DateValue, Menu } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { LucideMoreHorizontal } from 'lucide-react';

import { brokeredLoadMarkAsReceivedAtom } from '../../_graphql/mutationBrokeredLoadMarkAsReceived';
import { brokeredLoadMarkAsShippedAtom } from '../../_graphql/mutationBrokeredLoadMarkAsShipped';
import { brokeredLoadMarkAsVoidedAtom } from '../../_graphql/mutationBrokeredLoadMarkAsVoided';
import { useTranslations } from '../../i18n/useTranslations';
import { BrokeredLoadRecord } from '../../types/brokeredLoad';

import { BrokeredLoadMarkAsReceivedDialog } from './dialogs/BrokeredLoadMarkAsReceivedDialog';
import { BrokeredLoadMarkAsShippedDialog } from './dialogs/BrokeredLoadMarkAsShippedDialog';
import { BrokeredLoadMarkAsVoidedDialog } from './dialogs/BrokeredLoadMarkAsVoidedDialog';

import { LoadReconciliationWizard } from '~/modules/trade/_components/loads/_LoadReconciliationWizard';
import { loadReconcileAtom } from '~/modules/trade/_graphql/mutationLoadReconcile';

type Props = {
  load: BrokeredLoadRecord;
  onRefetch?: () => void;
};

const canMarkAsShipped = (status: LoadStatus) =>
  status === enumLoadStatus.Pending;

const canMarkAsReceived = (status: LoadStatus) =>
  status === enumLoadStatus.Shipped;

const canReconcile = (status: LoadStatus) => status === enumLoadStatus.Received;

const canVoid = (status: LoadStatus) =>
  status !== enumLoadStatus.Voided && status !== enumLoadStatus.Reconciled;

export const BrokeredLoadStatusTransition: React.FC<Props> = ({
  load,
  onRefetch,
}) => {
  const t = useTranslations();
  const toast = useToast();

  const [isShippedDialogOpen, setIsShippedDialogOpen] = React.useState(false);
  const [isReceivedDialogOpen, setIsReceivedDialogOpen] = React.useState(false);
  const [isReconcileWizardOpen, setIsReconcileWizardOpen] =
    React.useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = React.useState(false);

  const markAsShipped = useSetAtom(brokeredLoadMarkAsShippedAtom({}));
  const markAsReceived = useSetAtom(brokeredLoadMarkAsReceivedAtom({}));
  const markAsVoided = useSetAtom(brokeredLoadMarkAsVoidedAtom({}));
  const markAsReconciled = useSetAtom(loadReconcileAtom({}));

  const handleShipConfirm = React.useCallback(
    async (
      shippedDate: DateValue | undefined,
      lightWeight: Weight | null,
      heavyWeight: Weight | null,
    ) => {
      try {
        await markAsShipped({
          loadId: load.id,
          shippedDate: shippedDate ?? today(getLocalTimeZone()),
          lightWeight,
          heavyWeight,
        });
        toast.success(t.brokerage.loads.messages.shippedSuccess);
        onRefetch?.();
      } catch (error) {
        toast.error(
          `Failed to mark as shipped: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      } finally {
        setIsShippedDialogOpen(false);
      }
    },
    [load.id, markAsShipped, toast, t, onRefetch],
  );

  const handleReceiveConfirm = React.useCallback(
    async (receivedDate: DateValue | undefined) => {
      try {
        await markAsReceived({
          loadId: load.id,
          receivedDate: receivedDate ?? today(getLocalTimeZone()),
          receivedLineItems: [],
        });
        toast.success(t.brokerage.loads.messages.receivedSuccess);
        onRefetch?.();
      } catch (error) {
        toast.error(
          `Failed to mark as received: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      } finally {
        setIsReceivedDialogOpen(false);
      }
    },
    [load.id, markAsReceived, toast, t, onRefetch],
  );

  const handleReconcileConfirm = React.useCallback(async () => {
    try {
      await markAsReconciled({ id: load.id });
      toast.success(t.brokerage.loads.messages.reconciledSuccess);
      onRefetch?.();
    } catch (error) {
      toast.error(
        `Failed to reconcile: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setIsReconcileWizardOpen(false);
    }
  }, [load.id, markAsReconciled, toast, t, onRefetch]);

  const handleVoidConfirm = React.useCallback(async () => {
    try {
      await markAsVoided({ loadId: load.id });
      toast.success(t.brokerage.loads.messages.voidSuccess(load.loadNumber));
      onRefetch?.();
    } catch (error) {
      toast.error(
        `Failed to void: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setIsVoidDialogOpen(false);
    }
  }, [load.id, load.loadNumber, markAsVoided, toast, t, onRefetch]);

  const menuItems = React.useMemo(() => {
    const items: Array<{
      label: string;
      onAction: () => void;
    }> = [];

    if (canMarkAsShipped(load.status)) {
      items.push({
        label: t.brokerage.loads.actions.markAsShipped,
        onAction: () => setIsShippedDialogOpen(true),
      });
    }

    if (canMarkAsReceived(load.status)) {
      items.push({
        label: t.brokerage.loads.actions.markAsReceived,
        onAction: () => setIsReceivedDialogOpen(true),
      });
    }

    if (canReconcile(load.status)) {
      items.push({
        label: t.brokerage.loads.actions.reconcile,
        onAction: () => setIsReconcileWizardOpen(true),
      });
    }

    if (canVoid(load.status)) {
      items.push({
        label: t.brokerage.loads.actions.void,
        onAction: () => setIsVoidDialogOpen(true),
      });
    }

    return items;
  }, [load.status, t]);

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <>
      <Menu
        trigger={
          <Button variant="outline" icon={<LucideMoreHorizontal />}>
            {t.brokerage.loads.actions.actions}
          </Button>
        }
        items={menuItems}
      />

      {isShippedDialogOpen && (
        <BrokeredLoadMarkAsShippedDialog
          onMarkAsShipped={handleShipConfirm}
          onCancel={() => setIsShippedDialogOpen(false)}
        />
      )}

      {isReceivedDialogOpen && (
        <BrokeredLoadMarkAsReceivedDialog
          onMarkAsReceived={handleReceiveConfirm}
          onCancel={() => setIsReceivedDialogOpen(false)}
        />
      )}

      {isReconcileWizardOpen && (
        <LoadReconciliationWizard
          loadId={load.id}
          loadNumber={load.loadNumber}
          isOpen={isReconcileWizardOpen}
          onClose={() => setIsReconcileWizardOpen(false)}
          onReconcile={handleReconcileConfirm}
        />
      )}

      {isVoidDialogOpen && (
        <BrokeredLoadMarkAsVoidedDialog
          loadNumber={load.loadNumber}
          onConfirm={handleVoidConfirm}
          onCancel={() => setIsVoidDialogOpen(false)}
        />
      )}
    </>
  );
};
